import { FhirResourceTypes } from '@oh-vcp/components-common';
import { useMemo } from 'react';
import useSmartOnFhirResource, { fetchSmartOnFhirData } from '../services/useSmartOnFhirResource';
import { useQueries } from 'react-query';

type SupportingRef = {
  resourceType: string;
  id: string;
};

type ResourceMap = Partial<Record<string, unknown>>;

const useFhirContext = (fhirContext: string | undefined) => {
  const serviceRequestId = useMemo(() => {
    if (!fhirContext) return undefined;
    try {
      const fhirContextObj = JSON.parse(fhirContext);
      const reference = fhirContextObj?.[0]?.reference;
      if (!reference) return undefined;
      const [, id] = reference.split('/');
      return id;
    } catch {
      return undefined;
    }
  }, [fhirContext]);

  const { data: smartOnFhirContextDetails, isError: isServiceRequestError } =
    useSmartOnFhirResource(FhirResourceTypes.ServiceRequest, serviceRequestId);

  const supportingRefs: SupportingRef[] = useMemo(
    () =>
      (smartOnFhirContextDetails?.supportingInfo ?? []).map(
        ({ reference }: { reference: string }) => {
          const [resourceType, id] = reference.split('/');
          return { resourceType, id };
        }
      ),
    [smartOnFhirContextDetails]
  );

  const results = useQueries(
    supportingRefs.map(({ resourceType, id }) => ({
      queryKey: ['fhirResource', resourceType, id],
      queryFn: () => fetchSmartOnFhirData(resourceType, id),
      enabled: !!id,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    }))
  );

  // results from useQueries is always a new array reference on every render (React Query v3
  // behaviour). Using it directly as a useMemo dep would cause resourceMap to be recreated on
  // every render. Instead we use a stable fingerprint based on dataUpdatedAt so the memo only
  // recomputes when query data actually changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resultsDataKey = results.map((r) => r.dataUpdatedAt ?? 0).join(':');

  const resourceMap: ResourceMap = useMemo(
    () =>
      results.reduce<ResourceMap>((acc, result, index) => {
        const { resourceType } = supportingRefs[index];
        if (result.data) {
          const existing = acc[resourceType];
          acc[resourceType] = existing
            ? ([] as unknown[]).concat(existing, result.data)
            : result.data;
        }
        return acc;
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resultsDataKey, supportingRefs]
  );

  // The EMR tags each supporting reference with its own classification (SocialHistory,
  // TobaccoUse, AlcoholUse, ...). That tag is the only place the risk-factor grouping exists.
  const supportingInfoTypes = useMemo(() => {
    const types = new Map<string, string>();
    (smartOnFhirContextDetails?.supportingInfo ?? []).forEach(
      ({ reference, type }: { reference?: string; type?: string }) => {
        if (reference && type) types.set(reference, type);
      }
    );
    return types;
  }, [smartOnFhirContextDetails]);

  const isAwaitingServiceRequest =
    !!serviceRequestId && !isServiceRequestError && !smartOnFhirContextDetails;

  const isLoading = isAwaitingServiceRequest || results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);

  const notes: unknown[] = smartOnFhirContextDetails?.note ?? [];

  return { resourceMap, supportingInfoTypes, isLoading, isError, notes };
};

export default useFhirContext;
